import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { Table } from 'primeng/table';
import { Observable, Subject } from 'rxjs';
import { finalize, first, take, takeUntil } from 'rxjs/operators';
import _ from 'lodash';

import { Account } from '../../models/account';
import { FuneralCost } from '../../models/funeral-cost';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';
import { SiteService } from '../../services/site.service';

import { AccountFuneralCostComponent } from '../account-funeral-cost/account-funeral-cost.component';

/**
 * Funeral Costs tab — list view + CRUD for the account's public pricing tiers.
 *
 * Mirrors the AccountCharityListComponent pattern for the table/persistence
 * loop, but the per-item editor is dedicated (AccountFuneralCostComponent)
 * because funeral costs have multi-field bodies and nested items collections
 * that the generic inputNameValuePair modal can't cover.
 *
 * Storage: account.data.funeralCosts is an id-keyed object collection (matches
 * charities, eventTypes, etc.). Save is the existing accountService.patch
 * — no special endpoint needed.
 */
@Component({
  selector: 'app-account-funeral-cost-list',
  templateUrl: './account-funeral-cost-list.component.html',
  styleUrls: ['./account-funeral-cost-list.component.scss'],
})
export class AccountFuneralCostListComponent implements OnInit, OnDestroy {
  /** True until ensureFullAccount() resolves (see content lists). */
  accountLoading = true;

  private unsubscribe$ = new Subject<void>();

  account$: Observable<Account>;

  /** Region filter for the list — "All regions" + Global + the content regions. */
  regionFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All regions', value: null },
    { label: 'Global', value: 'global' },
  ];
  selectedRegion: string | null = null;

  constructor(
    private siteService: SiteService,
    private modalService: ModalService,
    private accountService: AccountService,
    private dialogService: DialogService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.account$ = this.accountService.account$;
  }

  ngOnInit(): void {
    // The default account fetch omits the content subtree (payload diet) and
    // the region dropdown reads content.regions — load it on demand, then
    // build the filter options (Global always present; "All regions" clears).
    this.accountService
      .ensureFullAccount()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.accountLoading = false;
          const regions = this.regions();
          this.regionFilterOptions = [
            { label: 'All regions', value: null },
            { label: 'Global', value: 'global' },
            ...regions
              .filter((r) => r && r.slug)
              .map((r) => ({ label: r.name || (r.slug as string), value: r.slug as string })),
          ];
        },
      });
  }

  /** Filter the table to one region (or clear it when "All regions"/null). */
  onRegionFilter(table: Table, value: string | null): void {
    table.filter(value, 'region', 'equals');
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Helper for the table — accepts `account.data.funeralCosts` and returns
   * the items as a stable sorted array. Soft-deleted items are kept so
   * authors can restore them.
   *
   * Defensive on shape because account.data is normalised client-side:
   * the same Conductor `account_data` EAV that the server stores as an
   * id-keyed object becomes an array on the client side
   * (objectsToArrays). Other places in the codebase might also write
   * to it directly, so handle both shapes.
   */
  funeralCostsAsList(data: { [id: string]: FuneralCost } | FuneralCost[] | undefined): FuneralCost[] {
    if (!data) return [];
    const list = Array.isArray(data)
      ? data.filter(Boolean)
      : Object.values(data).filter(Boolean);
    return _.sortBy(list, (p) => p?._meta?.index ?? Number.MAX_SAFE_INTEGER);
  }

  onAddFuneralCost(event: Event): void {
    // Generate a fresh id and seed the new package with default _meta
    // matching the convention (id-prefix + short suffix, integer index).
    const id = `id-pkg-${this.shortId()}`;
    const account = this.lastAccountSnapshot();
    if (!account) return;
    const existing = this.funeralCostsAsList(account.data?.funeralCosts);
    const nextIndex = this.computeNextIndex(existing);
    const seed: FuneralCost = {
      _meta: { id, index: nextIndex },
      slug: '',
      name: '',
      isActive: true,
      isFeatured: false,
    };
    this.openEditor(seed, true);
  }

  onEditFuneralCost(event: Event, pkg: FuneralCost): void {
    this.openEditor(_.cloneDeep(pkg), false);
  }

  onDeleteFuneralCost(event: Event, pkg: FuneralCost): void {
    const modal = this.modalService.generic({
      title: 'Remove funeral cost',
      copy: [
        `Are you sure you want to remove "${pkg.name || pkg._meta.id}"?`,
        'The package will stop appearing in new arrangements and on the public Picaluna site, but its URL will continue to render a "no longer available" notice so existing links don\'t 404.',
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Confirm', key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });

    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason?.toString().toLowerCase() === 'confirm') {
          pkg._meta.deleted = true;
          this.persistItem(pkg);
        }
      },
    });
  }

  onRestoreFuneralCost(event: Event, pkg: FuneralCost): void {
    pkg._meta.deleted = false;
    this.persistItem(pkg);
  }

  /**
   * Open the dedicated editor modal. On Save, the editor mutates the
   * passed-in object (Angular reactive form ↔ object bridge), so we just
   * re-persist account.data afterwards.
   */
  private openEditor(pkg: FuneralCost, isNew: boolean): void {
    const ref = this.dialogService.open(AccountFuneralCostComponent, {
      header: isNew ? 'Add package' : `Edit ${pkg.name || 'package'}`,
      width: '90vw',
      baseZIndex: 11000, // above the outer list modal
      data: {
        pkg,
        isNew,
        regions: this.regions(),
        // The editor runs this itself and closes only once the server has
        // confirmed — Save spins meanwhile. Without it the dialog vanished
        // instantly while the write was still in flight, so a publish could
        // ship a snapshot without the package that had just been "saved".
        save: (p: FuneralCost) => this.persistItem$(p),
      },
    });

    ref.onClose.subscribe({
      next: (result: { saved: boolean; pkg: FuneralCost; persisted?: boolean } | undefined) => {
        if (!result?.saved) return;
        const account = this.lastAccountSnapshot();
        if (!account) return;

        // CRITICAL: account.data on the client side is the post-objectsToArrays
        // shape — id-keyed collections like funeralCosts are arrays here, not
        // objects. accountService.patch() inverts them back to id-keyed
        // objects before sending. So we MUST do array operations here, or
        // the new package gets dropped during arraysToObjects.
        if (!Array.isArray(account.data.funeralCosts)) {
          account.data.funeralCosts = [];
        }
        const existingIndex = (account.data.funeralCosts as FuneralCost[]).findIndex(
          (p) => p?._meta?.id === result.pkg._meta.id
        );
        if (existingIndex >= 0) {
          (account.data.funeralCosts as FuneralCost[])[existingIndex] = result.pkg;
        } else {
          (account.data.funeralCosts as FuneralCost[]).push(result.pkg);
        }
        // The write already happened inside the editor (save callback above);
        // this only reconciles the local list. persistItem stays as the
        // fallback for a result that predates the callback.
        if (!result.persisted) this.persistItem(result.pkg);
      },
    });
  }

  /**
   * Per-item persistence via the RBAC-gated content route. Sends ONLY this
   * package (soft-delete flag included), so concurrent edits to different
   * funeral costs — or any other account data — can't clobber each other. The
   * generic account PATCH no longer accepts funeralCosts writes (write-protected).
   */
  /** The write itself, for the editor's save-then-close callback. */
  private persistItem$(pkg: FuneralCost) {
    this.siteService.addSubscriptionLog(
      this,
      'account-funeral-cost-list.component.ts->persistItem$->this.accountService.patchContentItem'
    );
    return this.accountService.patchContentItem('funeralCosts', pkg).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'account-funeral-cost-list.component.ts->persistItem$->this.accountService.patchContentItem'
        )
      ),
      take(1)
    );
  }

  private persistItem(pkg: FuneralCost): void {
    this.siteService.addSubscriptionLog(
      this,
      'account-funeral-cost-list.component.ts->persistItem->this.accountService.patchContentItem'
    );

    this.accountService
      .patchContentItem('funeralCosts', pkg)
      .pipe(
        finalize(() =>
          this.siteService.setSubscriptionLogFinalised(
            'account-funeral-cost-list.component.ts->persistItem->this.accountService.patchContentItem'
          )
        ),
        // take(1), NOT takeUntil(unsubscribe$) + first(). takeUntil on a WRITE
        // aborts the in-flight save when the modal closes AND completes the
        // stream empty, so first() throws EmptyError — the "no elements in
        // sequence" dialog. A content write must outlive its dialog; take(1)
        // completes on its own, so nothing leaks. (Fixed everywhere else
        // 2026-08-20; these two were missed.)
        take(1)
      )
      .subscribe({
        // Silence here meant a server rejection looked exactly like a
        // successful save — the row stayed on screen and the next reload
        // brought back the old value.
        error: (err: any) => this.modalService.error(err?.error?.error || err),
      });
  }

  /** Snapshot the current account so we can mutate it synchronously. */
  private lastAccountSnapshot(): Account | undefined {
    let snapshot: Account | undefined;
    this.accountService.account$.pipe(first()).subscribe({
      next: (a) => (snapshot = a),
    });
    return snapshot;
  }

  /** Content-module regions (client array) — options for the editor's Region
   * pricing dropdown. Empty until a Content Manager has added regions. */
  private regions(): Array<{ name?: string; slug?: string }> {
    const data = this.lastAccountSnapshot()?.data?.content?.regions;
    if (!data) return [];
    return Array.isArray(data) ? data.filter(Boolean) : Object.values(data).filter(Boolean);
  }

  /**
   * Pick the next integer index for a new package. Convention from
   * elsewhere in Conductor: gaps of 10 so drag-reorder insertions don't
   * trigger a mass reindex.
   */
  private computeNextIndex(existing: FuneralCost[]): number {
    const max = existing.reduce((acc, p) => {
      const i = p?._meta?.index;
      return typeof i === 'number' && i > acc ? i : acc;
    }, -10);
    return max + 10;
  }

  /** Short, URL-safe id suffix — random 8 chars. */
  private shortId(): string {
    return Math.random().toString(36).slice(2, 10);
  }
}
